import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Loader2, MessageSquare, Building2, Hash } from "lucide-react";
import { SupportConversation } from "@/components/support/SupportConversation";
import { STATUS_META, PRIORITY_META, fmtDateTime, fmtTicketNo } from "@/components/support/supportUtils";

export const SupportAdminSection = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await api.get("/admin/support/tickets", { params });
      setTickets(r.data || []);
    } catch { /* silenzioso */ }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openCount = tickets.reduce((s, t) => s + (t.unread > 0 ? 1 : 0), 0);

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="support-admin-section">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <LifeBuoy className="h-4 w-4 text-[#007BFF]" />
          <h3 className="font-head text-lg font-semibold">Assistenza — ticket degli amministratori</h3>
          {openCount > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white" data-testid="support-admin-unread">{openCount} da leggere</span>}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="support-admin-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">Tutti gli stati</SelectItem>
            <SelectItem value="aperto" className="text-xs">Aperti</SelectItem>
            <SelectItem value="in_lavorazione" className="text-xs">In lavorazione</SelectItem>
            <SelectItem value="risolto" className="text-xs">Risolti</SelectItem>
            <SelectItem value="chiuso" className="text-xs">Chiusi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected ? (
        <div className="border border-border rounded-lg p-4">
          <SupportConversation ticketId={selected} isSuper={true} onBack={() => { setSelected(null); load(); }} onChanged={load} />
        </div>
      ) : loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm" data-testid="support-admin-empty">Nessun ticket {statusFilter !== "all" ? "con questo stato" : ""}.</div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => {
            const st = STATUS_META[t.status] || STATUS_META.aperto;
            const pr = PRIORITY_META[t.priority] || PRIORITY_META.media;
            const studio = t.studio || {};
            const studioName = studio.ragione_sociale || t.admin_name || t.admin_email || "Studio senza nome";
            const meta = [studio.citta && studio.provincia ? `${studio.citta} (${studio.provincia})` : studio.citta,
                          studio.partita_iva ? `P.IVA ${studio.partita_iva}` : "",
                          studio.telefono].filter(Boolean);
            return (
              <button key={t.id} data-testid={`admin-ticket-row-${t.id}`} onClick={() => setSelected(t.id)}
                className="w-full text-left border border-border rounded-lg p-3 hover:border-[#007BFF]/50 transition-colors flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-[#007BFF]/10 flex items-center justify-center shrink-0"><MessageSquare className="h-4 w-4 text-[#007BFF]" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span data-testid={`admin-ticket-no-${t.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-white">
                      <Hash className="h-3 w-3" />{fmtTicketNo(t.ticket_no).replace("#", "")}
                    </span>
                    <span className="font-semibold text-sm truncate">{t.subject}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${pr.cls}`}>{pr.label}</span>
                    {t.unread > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white">{t.unread} nuovo{t.unread === 1 ? "" : "i"}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-foreground/90 mt-1">
                    <Building2 className="h-3.5 w-3.5 text-[#007BFF] shrink-0" />
                    <span className="truncate" data-testid={`admin-ticket-studio-${t.id}`}>{studioName}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {t.admin_email}{meta.length ? ` · ${meta.join(" · ")}` : ""}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">{t.category} · {t.last_message_preview || "—"}</div>
                  <div className="text-[10px] text-muted-foreground/70 mt-0.5">Aggiornato il {fmtDateTime(t.last_message_at)}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
